import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_BASE_URL || "http://localhost:3000/api/v1";

const UserManagement = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = sessionStorage.getItem("userManagementPage");
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    sessionStorage.setItem("userManagementPage", currentPage.toString());
  }, [currentPage]);

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      navigate("/login");
      return;
    }

    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          page: currentPage.toString(),
          limit: "20",
        });

        if (searchQuery.trim()) {
          params.append("search", searchQuery.trim());
        }

        const response = await axios.get(
          `${API_BASE_URL}/admin/users/filter?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        // ✅ 401 CHECK
        if (
          response.data.statusCode === 401 ||
          response.data.message === "Unauthorized"
        ) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          window.location.href = "/login";
          return;
        }

        const sortedUsers = (response.data.users || []).sort((a, b) => {
          const balanceA = a.wallet?.balance || 0;
          const balanceB = b.wallet?.balance || 0;
          return balanceB - balanceA;
        });

        setUsers(sortedUsers);
        setTotalPages(response.data.totalPages || 1);
      } catch (err) {
        // ✅ 401 CHECK IN CATCH
        if (
          err.response?.status === 401 ||
          err.response?.data?.message === "Unauthorized"
        ) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          window.location.href = "/login";
          return;
        }

        console.error("Failed to fetch users:", err);
        const errorMessage =
          err.response?.data?.message ||
          "Failed to load user data. Please try again later.";
        setError(errorMessage);
        setUsers([]);
      } finally {
        setLoading(false);
        setIsSearching(false);
      }
    };

    fetchUsers();
  }, [currentPage, navigate, searchQuery]);

  const handleUserClick = (userId) => {
    navigate(`/slay-details/${userId}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setIsSearching(true);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
    setIsSearching(false);
  };

  const exchangeRates = [
    { token: "1 SLAY", value: "0.0001 BTC", icon: "₿" },
    { token: "1 SLAY", value: "0.000001 ETH", icon: "Ξ" },
    { token: "1 SLAY", value: "0.000000 SOL", icon: "◎" },
    { token: "1 SLAY", value: "0.0001 BTC", icon: "₿" },
    { token: "1 SLAY", value: "0.000001 ETH", icon: "Ξ" },
    { token: "1 SLAY", value: "0.000000 SOL", icon: "◎" },
    { token: "1 SLAY", value: "0.0001 BTC", icon: "₿" },
    { token: "1 SLAY", value: "0.000001 ETH", icon: "Ξ" },
  ];

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? "bg-black text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="flex flex-col gap-5 p-5">
        {/* Page Title and Search */}
        <div className="flex items-center justify-between gap-4">
          <h2
            className={`text-2xl font-bold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            User Management
          </h2>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, or username..."
                className={`w-full px-4 py-2 pr-20 rounded-lg border ${
                  isDarkMode
                    ? "bg-zinc-900 border-zinc-800 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                } focus:outline-none focus:ring-2 focus:ring-orange-400`}
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {searchQuery && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className={`p-1 rounded ${
                      isDarkMode
                        ? "hover:bg-zinc-800 text-gray-400"
                        : "hover:bg-gray-100 text-gray-600"
                    }`}
                  >
                    <X size={16} />
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSearching}
                  className={`p-1.5 rounded ${
                    isDarkMode
                      ? "bg-orange-400 text-black hover:bg-orange-500"
                      : "bg-orange-400 text-black hover:bg-orange-500"
                  } disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                >
                  <Search size={16} />
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Search Results Info */}
        {searchQuery && (
          <div
            className={`px-4 py-2 rounded-lg ${
              isDarkMode ? "bg-zinc-900" : "bg-gray-200"
            }`}
          >
            <p
              className={`text-sm ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {loading ? (
                "Searching..."
              ) : (
                <>
                  Found {users.length} result{users.length !== 1 ? "s" : ""} for{" "}
                  <span className="font-semibold">"{searchQuery}"</span>
                </>
              )}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/50 border border-red-500 rounded-xl p-4 text-red-200">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div
            className={`lg:col-span-2 ${
              isDarkMode
                ? "bg-black border-zinc-800"
                : "bg-white border-gray-200"
            } border rounded-xl p-5`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr
                    className={`border-b ${
                      isDarkMode ? "border-zinc-800" : "border-gray-200"
                    }`}
                  >
                    <th
                      className={`text-left ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      } text-sm font-medium py-4 px-2`}
                    >
                      <div className="flex items-center gap-1">User</div>
                    </th>
                    <th
                      className={`text-left ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      } text-sm font-medium py-4 px-2`}
                    >
                      Total Tokens Balance
                    </th>
                    <th
                      className={`text-left ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      } text-sm font-medium py-4 px-2`}
                    >
                      Action for Tokens
                    </th>
                    <th className="py-4 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="4"
                        className={`text-center py-10 ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Loading users...
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td
                        colSpan="4"
                        className={`text-center py-10 ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {searchQuery
                          ? "No users found matching your search."
                          : "No users found."}
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => {
                      const status =
                        user.status.charAt(0).toUpperCase() +
                        user.status.slice(1);

                      const avatar = `${user.firstName?.charAt(0) || ""}${
                        user.lastName?.charAt(0) || ""
                      }`.toUpperCase();

                      const role = user.role || "user";
                      const roleDisplay =
                        role.charAt(0).toUpperCase() + role.slice(1);

                      const fullName = `${user.firstName} ${user.lastName}`;
                      const displayName =
                        fullName.length > 15
                          ? fullName.slice(0, 15) + "..."
                          : fullName;

                      return (
                        <tr
                          key={user._id}
                          className={`border-b ${
                            isDarkMode
                              ? "border-zinc-800 hover:bg-zinc-900"
                              : "border-gray-200 hover:bg-gray-50"
                          } transition-colors cursor-pointer`}
                        >
                          <td
                            className="py-4 px-2"
                            onClick={() => handleUserClick(user._id)}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                {avatar}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p
                                    className={`${
                                      isDarkMode
                                        ? "text-white"
                                        : "text-gray-900"
                                    } font-medium`}
                                  >
                                    {displayName}
                                  </p>
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      role === "admin"
                                        ? "bg-purple-500/20 text-purple-400"
                                        : isDarkMode
                                        ? "bg-zinc-800 text-gray-400"
                                        : "bg-gray-200 text-gray-600"
                                    }`}
                                  >
                                    {roleDisplay}
                                  </span>
                                </div>
                                <p
                                  className={`${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-600"
                                  } text-sm`}
                                >
                                  {user.email || "No contact info"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td
                            className={`py-4 px-2 ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            } font-medium`}
                            onClick={() => handleUserClick(user._id)}
                          >
                            {user.wallet?.balance.toLocaleString("en-IN") ||
                              "0"}
                          </td>
                          <td
                            className="py-4 px-2"
                            onClick={() => handleUserClick(user._id)}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                                  status === "Active"
                                    ? "bg-orange-400 text-black"
                                    : isDarkMode
                                    ? "bg-zinc-800 text-gray-400"
                                    : "bg-gray-200 text-gray-600"
                                }`}
                              >
                                <span
                                  className={`w-2 h-2 rounded-full ${
                                    status === "Active"
                                      ? "bg-black"
                                      : isDarkMode
                                      ? "bg-gray-600"
                                      : "bg-gray-400"
                                  }`}
                                ></span>
                                {status}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-2">
                            <button
                              onClick={() => handleUserClick(user._id)}
                              className={`${
                                isDarkMode
                                  ? "text-gray-400 hover:text-white"
                                  : "text-gray-600 hover:text-gray-900"
                              } transition-colors`}
                            >
                              <ChevronRight size={20} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div
              className={`flex items-center justify-center gap-4 mt-5 pt-5 border-t ${
                isDarkMode ? "border-zinc-800" : "border-gray-200"
              }`}
            >
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1 || loading}
                className={`${
                  isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                } disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
              >
                <ChevronLeft size={20} />
              </button>
              <span
                className={`${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                } text-sm`}
              >
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages || loading}
                className={`${
                  isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                } disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          <div
            className={`${
              isDarkMode
                ? "bg-black border-zinc-800"
                : "bg-white border-gray-200"
            } border rounded-xl p-5`}
          >
            <div className="flex items-center justify-between mb-5">
              <h3
                className={`${
                  isDarkMode ? "text-white" : "text-gray-900"
                } font-semibold`}
              >
                Current Token Price/Exchange Rate
              </h3>
            </div>

            <div className="space-y-3 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent">
              {exchangeRates.map((rate, idx) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between p-3 ${
                    isDarkMode
                      ? "bg-zinc-900 hover:bg-zinc-800"
                      : "bg-gray-50 hover:bg-gray-100"
                  } rounded-lg transition-colors`}
                >
                  <span
                    className={`${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    } text-sm`}
                  >
                    {rate.token}
                  </span>
                  <div className="flex items-center gap-2">
                    <span
                      className={`${
                        isDarkMode ? "text-white" : "text-gray-900"
                      } font-medium text-sm`}
                    >
                      {rate.value}
                    </span>
                    <div className="w-6 h-6 bg-orange-400 rounded-full flex items-center justify-center text-black text-xs font-bold">
                      {rate.icon}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
